import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon, PlusIcon, PaletteIcon, BookIcon, BriefcaseIcon, GlobeIcon, StoreIcon } from 'lucide-react';
import type { PM } from './useProductManager';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
interface ConfirmationScreenProps {
  pm: PM;
}
export const ConfirmationScreen: React.FC<ConfirmationScreenProps> = ({
  pm
}) => {
  const dk = useDarkClasses();
  const {
    darkMode,
    productType,
    mode,
    savedAsDraft,
    selectedItemId,
    bookForm,
    merchForm,
    eventForm,
    bookBoxForm,
    creativeForm,
    serviceForm,
    setShowConfirmation,
    handleTypeSelect,
    setProductType,
    publishToAfterTide
  } = pm;
  const itemName = productType === 'book' ? bookForm.title || 'Untitled Book' : productType === 'merch' ? merchForm.name || 'Untitled Product' : productType === 'event' ? eventForm.title || 'Untitled Event' : productType === 'bookbox' ? bookBoxForm.boxName || 'Untitled Box' : productType === 'creative' ? creativeForm.displayName || 'Untitled Profile' : serviceForm.displayName || 'Untitled Service';
  return <div className={`flex flex-col items-center justify-center py-16 px-4 rounded-xl border ${dk.card}`} data-id="element-20978" data-mp="1mcan29g27fle">
      <div className={`mb-6 rounded-full p-4 ${darkMode ? 'bg-green-900/30' : 'bg-green-100'}`} data-id="element-20979" data-mp="2e5unpe98bhpm">
        <CheckCircleIcon className="w-16 h-16 text-green-500 animate-[bounce_1s_ease-in-out]" data-id="element-20980" data-mp="2a3puuhqlup41" />
      </div>
      <h2 className={`text-2xl font-bold mb-2 text-center ${dk.textHeading}`} data-id="element-20981" data-mp="254d8dy3nbk1z">
        {savedAsDraft ? 'Draft Saved!' : mode === 'create' ? 'Published Successfully!' : 'Changes Saved!'}
      </h2>
      <p className={`text-center mb-8 max-w-md ${dk.textSecondary}`} data-id="element-20982" data-mp="trar10tq2qgr">
        Your {productType} has been successfully{' '}
        {savedAsDraft ? 'saved as a draft' : 'published to the marketplace'}.
        You can view and manage it from your dashboard.
      </p>

      {publishToAfterTide && !savedAsDraft && <div className="mb-8 p-4 rounded-xl bg-[#1a1a2e] border border-[#e2b04a]/30 max-w-md w-full" data-id="element-20983" data-mp="v4sngbpklkzh">
          <h3 className="text-[#e2b04a] font-bold mb-2 flex items-center" data-id="element-20984" data-mp="h9q52p3je35s">
            <span className="mr-2" data-id="element-20985" data-mp="m4nfo0ggskks">✨</span>
            Also Available on AfterTide
          </h3>
          <p className="text-sm text-gray-300" data-id="element-20986" data-mp="7g9d7qmymy4r">
            Your book has been successfully published to AfterTide with its
            darkness tier and consent framework metadata.
          </p>
        </div>}

      <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md" data-id="element-20987" data-mp="3ukgv0bz27q09">
        {!savedAsDraft && (productType === 'book' || productType === 'bookbox' || productType === 'creative' || productType === 'service') && <Link to={productType === 'book' ? `/book/${selectedItemId || 'new'}` : productType === 'bookbox' ? `/book-boxes/${encodeURIComponent(bookBoxForm.boxName || '')}` : productType === 'creative' ? `/creative/cover-artists/${selectedItemId || 'new'}` : `/services/editing/${selectedItemId || 'new'}`} className={`col-span-1 sm:col-span-2 flex items-center justify-center px-6 py-3 rounded-lg text-white font-medium transition-colors ${productType === 'book' ? 'bg-purple-600 hover:bg-purple-700' : productType === 'bookbox' ? 'bg-pink-600 hover:bg-pink-700' : productType === 'creative' ? 'bg-violet-500 hover:bg-violet-600' : 'bg-blue-500 hover:bg-blue-600'}`} data-id="element-20988" data-mp="3plvwqi3dbgeg">
              <GlobeIcon className="w-5 h-5 mr-2" data-id="element-20989" data-mp="bkv66ae315sd" />
              View Public Page
            </Link>}
        <button onClick={() => {
        setShowConfirmation(false);
        handleTypeSelect(productType!);
      }} className={`flex items-center justify-center px-6 py-3 rounded-lg border font-medium transition-colors ${dk.border} ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} ${dk.textPrimary}`} data-id="element-20990" data-mp="7kkoi0gxgqxo">
          <PlusIcon className="w-5 h-5 mr-2" data-id="element-20991" data-mp="2auehojuv6vaw" />
          Create Another
        </button>
        <button onClick={() => {
        setShowConfirmation(false);
        setProductType(null);
      }} className={`flex items-center justify-center px-6 py-3 rounded-lg border font-medium transition-colors ${dk.border} ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} ${dk.textPrimary}`} data-id="element-20992" data-mp="2fqlenn5a8wgp">
          <PaletteIcon className="w-5 h-5 mr-2" data-id="element-20993" data-mp="1w35pc2zkuvu2" />
          Create Different Type
        </button>
        <Link to="/manage-listings" className={`flex items-center justify-center px-6 py-3 rounded-lg border font-medium transition-colors ${dk.border} ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} ${dk.textPrimary}`} data-id="element-20994" data-mp="33h907b84fklq">
          <BookIcon className="w-5 h-5 mr-2" data-id="element-20995" data-mp="vufmr9vmnc7q" />
          Back to Listings
        </Link>
        <Link to="/vendors/dashboard" className={`flex items-center justify-center px-6 py-3 rounded-lg border font-medium transition-colors ${dk.border} ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} ${dk.textPrimary}`} data-id="element-20996" data-mp="ru5ko8ayh5y5">
          <BriefcaseIcon className="w-5 h-5 mr-2" data-id="element-20997" data-mp="d5939kn0egkv" />
          Back to Dashboard
        </Link>
      </div>
    </div>;
};